import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  MoreVertical,
  Save,
  X,
  FolderOpen,
  Tag,
  Calendar,
  User,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");

  // Fetch categories and types on component mount
  useEffect(() => {
    fetchCategories();
    fetchTypes();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/category/all`);
      setCategories(data.data);
    } catch (error) {
      toast.error("Failed to fetch categories");
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const { data } = await axios.get(`${server}/type/all`);
      setTypes(data.data || []);
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    if (!selectedTypeId) {
      toast.error("Please select a type");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(`${server}/category/new`, null, {
        params: {
          category: newCategory.trim(),
          typeId: selectedTypeId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Category added successfully!");
        setNewCategory("");
        setSelectedTypeId("");
        setShowAddModal(false);
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCategory = async (category) => {
    if (!newCategory.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    if (!selectedTypeId) {
      toast.error("Please select a type");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(`${server}/category/updateById`, null, {
        params: {
          categoryId: category.id,
          category: newCategory.trim(),
          typeId: selectedTypeId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Category updated successfully!");
        setEditingCategory(null);
        setNewCategory("");
        setSelectedTypeId("");
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategoryPermanently = async (categoryId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(
        `${server}/category/deleteForeverById`,
        {
          params: {
            categoryId: categoryId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Category deleted!");
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/category/deleteById`, {
        params: {
          categoryId: categoryId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Category deleted!");
        fetchCategories();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreCategory = async (categoryId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${server}/category/recoverDeletedById`,
        null,
        {
          params: {
            categoryId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Category restored successfully!");
        fetchCategories();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to restore category"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Filter categories based on search, type filter, and deleted status
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      category.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.slug.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedTypeFilter
      ? category.typeId === parseInt(selectedTypeFilter)
      : true;

    const matchesDeleted = showDeleted ? category.deleted : !category.deleted;

    return matchesSearch && matchesType && matchesDeleted;
  });

  // Get type name by ID
  const getTypeName = (typeId) => {
    const type = types.find((t) => t.id === typeId);
    return type ? type.type : "Unknown Type";
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTypeFilter("");
    setShowDeleted(false);
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || selectedTypeFilter || showDeleted;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const startEdit = (category) => {
    setEditingCategory(category);
    setNewCategory(category.category);
    setSelectedTypeId(category.typeId || "");
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setNewCategory("");
    setSelectedTypeId("");
  };

  const resetModal = () => {
    setNewCategory("");
    setSelectedTypeId("");
    setShowAddModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-lg">
              <FolderOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Category Manager</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage your question categories. Create, edit, and organize
            categories for better content management.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search and Filters */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Type Filter */}
                <div className="sm:w-48">
                  <div className="relative">
                    <select
                      value={selectedTypeFilter}
                      onChange={(e) => setSelectedTypeFilter(e.target.value)}
                      className="w-full pl-3 pr-10 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                    >
                      <option value="">All Types</option>
                      {types.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.type}
                        </option>
                      ))}
                    </select>
                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-wrap gap-3">
              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-600 text-gray-300 border border-gray-500 rounded-xl hover:bg-gray-500 transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}

              {/* Show Deleted Toggle */}
              <button
                onClick={() => setShowDeleted(!showDeleted)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                  showDeleted
                    ? "bg-red-500/20 text-red-400 border border-red-500/30"
                    : "bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600"
                }`}
              >
                {showDeleted ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                {showDeleted ? "Hide Deleted" : "Show Deleted"}
              </button>

              {/* Refresh */}
              <button
                onClick={fetchCategories}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              {/* Add Category */}
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>
          </div>

          {/* Active Filters Indicator */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-xs">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm("")}
                    className="hover:text-blue-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedTypeFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs">
                  Type: {getTypeName(parseInt(selectedTypeFilter))}
                  <button
                    onClick={() => setSelectedTypeFilter("")}
                    className="hover:text-green-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {showDeleted && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-xs">
                  Showing Deleted
                  <button
                    onClick={() => setShowDeleted(false)}
                    className="hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* Categories List */}
        <motion.div variants={itemVariants}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
              <FolderOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                {hasActiveFilters
                  ? "No categories match your filters"
                  : showDeleted
                  ? "No deleted categories found"
                  : "No categories found"}
              </h3>
              <p className="text-gray-500">
                {hasActiveFilters
                  ? "Try adjusting your filters or search terms"
                  : showDeleted
                  ? "Deleted categories will appear here"
                  : "Get started by creating your first category"}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredCategories.map((category) => (
                <motion.div
                  key={category.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`bg-gray-800 rounded-2xl p-6 border transition-all duration-200 ${
                    category.deleted
                      ? "border-red-500/30 bg-red-500/5"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Category Info */}
                    <div className="flex-1">
                      {editingCategory?.id === category.id ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              value={newCategory}
                              onChange={(e) => setNewCategory(e.target.value)}
                              className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter category name..."
                              autoFocus
                            />
                          </div>
                          <div className="relative">
                            <select
                              value={selectedTypeId}
                              onChange={(e) =>
                                setSelectedTypeId(e.target.value)
                              }
                              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                            >
                              <option value="">Select Type</option>
                              {types.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.type}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-white">
                              {category.category}
                            </h3>
                            {category.deleted && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                                Deleted
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <Tag className="w-4 h-4" />
                              <span>Slug: {category.slug}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FolderOpen className="w-4 h-4" />
                              <span>Type: {getTypeName(category.typeId)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(category.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>By User {category.createdBy}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {editingCategory?.id === category.id ? (
                        <>
                          <button
                            onClick={() => handleEditCategory(category)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </>
                      ) : category.deleted ? (
                        <>
                          <button
                            onClick={() => handleRestoreCategory(category.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Restore
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteCategoryPermanently(category.id)
                            }
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Permanently
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(category)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Stats */}
        {!loading && categories.length > 0 && (
          <motion.div
            className="mt-6 text-center text-gray-400 text-sm"
            variants={itemVariants}
          >
            Showing {filteredCategories.length} of {categories.length}{" "}
            categories
            {hasActiveFilters && " (filtered)"}
            {showDeleted && " (including deleted)"}
          </motion.div>
        )}
      </motion.div>

      {/* Add Category Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-slate-950 bg-opacity-90 rounded-3xl flex justify-center p-4 z-50"
            onClick={() => resetModal()}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-gray-800 rounded-2xl h-fit p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New Category
                </h3>
                <button
                  onClick={() => resetModal()}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter category name..."
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Type
                  </label>
                  <div className="relative">
                    <select
                      value={selectedTypeId}
                      onChange={(e) => setSelectedTypeId(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                    >
                      <option value="">Select Type</option>
                      {types.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.type}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => resetModal()}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {submitting ? "Adding..." : "Add Category"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryManager;
